import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const FilterContext = createContext();

export const useFilters = () => useContext(FilterContext);

const DEFAULT_FILTERS = {
  dateRange: { start: null, end: null },
  claimType: 'All',
  claimStatus: 'All',
  relation: 'All',
  cityType: 'All',
  employeeId: '',
  sumInsured: 'All',
};

export const FilterProvider = ({ children }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [drillFilters, setDrillFilters] = useState({});
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [clientInfo, setClientInfo] = useState({ clientName: '-', policyStart: '-', policyEnd: '-' });
  const [totalClaims, setTotalClaims] = useState(0);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setDrillFilters({});
  }, []);

  const setDrillFilter = useCallback((key, value) => {
    setDrillFilters(prev => {
      if (prev[key] === value) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const clearDrillFilter = useCallback((key) => {
    setDrillFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clearAllDrillFilters = useCallback(() => {
    setDrillFilters({});
  }, []);

  const loadData = useCallback((info, count) => {
    setClientInfo(info);
    setTotalClaims(count);
    setIsDataLoaded(true);
  }, []);

  // A version counter that increments whenever filters change — used by dashboard to trigger refetch
  const filterVersion = useMemo(() => {
    return JSON.stringify({ filters, drillFilters });
  }, [filters, drillFilters]);

  const value = useMemo(() => ({
    filters,
    updateFilter,
    resetFilters,
    isDataLoaded,
    loadData,
    clientInfo,
    totalClaims,
    drillFilters,
    setDrillFilter,
    clearDrillFilter,
    clearAllDrillFilters,
    filterVersion,
  }), [filters, updateFilter, resetFilters, isDataLoaded, loadData, clientInfo, totalClaims, drillFilters, setDrillFilter, clearDrillFilter, clearAllDrillFilters, filterVersion]);

  return (
    <FilterContext.Provider value={value}>
      {children}
    </FilterContext.Provider>
  );
};
